import { connect } from 'react-redux';
import { useMemo } from 'react';

import { getUserRoleIsProsecutorOrSuperProsecutor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ErknmFieldMark } from 'KnmErknmPackage/MainModule/components/ErknmFieldMark';
import { IKnmErknmFormChangePlanReason } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormChangePlanReason';
import { Section } from 'UIPackage/FormModule/components/Section';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { Row } from 'UIPackage/FormModule/components/Row';
import { geCatalogsKnmPlanReasonChangeTypesSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnmReasonChangeTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmReasonChangeTypeSelectOption';
import { KnmReasonChangeTypeOperationEnum } from 'APIPackage/CatalogsModule/enums/KnmReasonChangeTypeOperationEnum';
import { approvedChangeKnmSelectOptions } from 'KnmErknmPackage/MainModule/constants/approvedSelectOptions';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';

import styles from './ErknmApproveInfoDetails.module.scss';
import { ErknmApproveInfoDocument } from './ErknmApproveInfoDocument';

export interface IErknmApproveChangeKnmReason {
  reason: IKnmErknmFormChangePlanReason;
  // Индекс причины в полном массиве changePlanReason формы
  formIndex: number;
}

interface IErknmApproveInfoDetailsProps {
  changePlanReason: IErknmApproveChangeKnmReason[] | undefined;
  isKnmStatusApproving?: boolean;
}

interface IErknmApproveInfoDetailsStoreProps {
  isProsecutorOrSuperProsecutor: boolean;
  knmPlanReasonChangeTypesOptions: IKnmReasonChangeTypeSelectOption[];
  formRef: IKnmFormRef | undefined;
}

const ErknmApproveChangeKnmWithoutStore = ({
  changePlanReason,
  knmPlanReasonChangeTypesOptions,
  isProsecutorOrSuperProsecutor,
  formRef,
  isKnmStatusApproving,
}: IErknmApproveInfoDetailsProps & IErknmApproveInfoDetailsStoreProps) => {
  const reasons = changePlanReason ?? [];
  const knmPlanReasonChangeFilteredOptions = useMemo(
    () =>
      knmPlanReasonChangeTypesOptions.filter(option => option.operation === KnmReasonChangeTypeOperationEnum.CHANGE),
    [knmPlanReasonChangeTypesOptions],
  );

  const approvalStates = useMemo(() => reasons.map(({ reason }) => reason.approvedChangePlanErknm != null), [reasons]);

  const isFiledDisabled = useMemo(() => {
    if (!isProsecutorOrSuperProsecutor) {
      return true;
    }

    return !isKnmStatusApproving;
  }, [isProsecutorOrSuperProsecutor, isKnmStatusApproving]);

  return (
    <Section
      title={
        <>
          Внесение изменений в КНМ
          <ErknmFieldMark sectionName="approveInfo" />
        </>
      }
      id={'knm-approve-change-knm'}
      type="group"
    >
      {reasons.map(({ reason, formIndex }, idx) => (
        <KnmCollapse
          theme="fields"
          title={`Изменение №${idx + 1}`}
          id={`knm-approve-change-knm-${formIndex}`}
          key={String(formIndex)}
          opened={true}
        >
          <Row key={idx}>
            <FieldBlock
              label="Изменение КНМ по причине"
              id={`knm-approve-change-knm-reason-${formIndex}`}
              color="black"
            >
              <SelectInputField
                id="changePlanReason"
                name={`changePlanReason[${formIndex}].reasonChangeType`}
                value={reason.reasonChangeType}
                options={knmPlanReasonChangeFilteredOptions}
                disabled={true}
                size="medium"
              />
            </FieldBlock>
            <div className={styles.MarginBottom} />
            <Row>
              <FieldBlock
                label={
                  <>
                    Принять изменения в КНМ
                    <ErknmFieldMark fieldName={`changePlanReason[${formIndex}].approvedChangePlanErknm`} />
                  </>
                }
                id="knm-approved-change"
                color="black"
                size="half"
              >
                <SelectInputField
                  id={`changePlanReason[${formIndex}].approvedChangePlanErknm`}
                  name={`changePlanReason[${formIndex}].approvedChangePlanErknm`}
                  options={approvedChangeKnmSelectOptions}
                  size="medium"
                  disabled={!isProsecutorOrSuperProsecutor || idx !== reasons.length - 1 || !isKnmStatusApproving}
                  isClearable={false}
                />
              </FieldBlock>
              {approvalStates[idx] && (
                <FieldBlock size="half">
                  <Row>
                    <FieldBlock
                      label={
                        <>
                          Номер решения
                          <ErknmFieldMark
                            fieldName={`changePlanReason[${formIndex}].approveChangePlanNumberDecision`}
                          />
                        </>
                      }
                      color="black"
                      size="half"
                    >
                      <TextInputField
                        id={`changePlanReason[${formIndex}].approveChangePlanNumberDecision`}
                        name={`changePlanReason[${formIndex}].approveChangePlanNumberDecision`}
                        disabled={isFiledDisabled}
                      />
                    </FieldBlock>
                    <FieldBlock
                      label={
                        <>
                          Дата решения
                          <ErknmFieldMark fieldName={`changePlanReason[${formIndex}].approveChangePlanDateDecision`} />
                        </>
                      }
                      color="black"
                      size="half"
                    >
                      <DatePickerField
                        id={`changePlanReason[${formIndex}].approveChangePlanDateDecision`}
                        name={`changePlanReason[${formIndex}].approveChangePlanDateDecision`}
                        disabled={isFiledDisabled}
                      />
                    </FieldBlock>
                  </Row>
                </FieldBlock>
              )}
            </Row>
            {approvalStates[idx] && (
              <Row>
                <FieldBlock
                  label={
                    <>
                      Кем принято решение
                      <ErknmFieldMark fieldName={`changePlanReason[${formIndex}].approveChangePlanFioSigner`} />
                    </>
                  }
                  color="black"
                  size="half"
                >
                  <TextareaField
                    id={`changePlanReason[${formIndex}].approveChangePlanFioSigner`}
                    name={`changePlanReason[${formIndex}].approveChangePlanFioSigner`}
                    disabled={isFiledDisabled}
                  />
                </FieldBlock>
                <FieldBlock
                  label={
                    <>
                      Должность лица, подписавшего решение
                      <ErknmFieldMark fieldName={`changePlanReason[${formIndex}].approveChangePlanTitleSigner`} />
                    </>
                  }
                  color="black"
                  size="half"
                >
                  <TextareaField
                    id={`changePlanReason[${formIndex}].approveChangePlanTitleSigner`}
                    name={`changePlanReason[${formIndex}].approveChangePlanTitleSigner`}
                    disabled={isFiledDisabled}
                  />
                </FieldBlock>
              </Row>
            )}
            {approvalStates[idx] && (
              <ErknmApproveInfoDocument
                name={`changePlanReason[${formIndex}].approveChangePlanFile`}
                disableToAdd={false}
                formRef={formRef}
                watchOnly={isFiledDisabled}
              />
            )}
          </Row>
        </KnmCollapse>
      ))}
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IErknmApproveInfoDetailsStoreProps => ({
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  knmPlanReasonChangeTypesOptions: geCatalogsKnmPlanReasonChangeTypesSelectOptions(state),
  formRef: getKnmFormRef(state),
});

export const ErknmApproveChangeKnm = connect(mapStateToProps)(ErknmApproveChangeKnmWithoutStore);
